import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useAuthProvider } from '../../utils/auth';
import { AppRoutes } from '../../constants/routes';

/**
 * Route guard for artists' tools and label and publisher pages
 */
const CreatorOnlyRoute = ({ children }) => {
  const { isAuthed, user, userType, actingAs, isLoading, workAsOrganization } = useAuthProvider();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthed) {
    return <Navigate to={AppRoutes.LoginPage} state={{ from: location }} replace />;
  }

  // Artists' tools, and the label and publisher pages: creators, and anyone
  // working as a label or publisher they run
  if (userType === 'creator' || actingAs) {
    return children;
  }

  // Someone else: switch to a label or publisher they run, or start
  // releasing music - from the same account
  const organizations = [...(user?.organizations?.labels || []).map((org) => ({ ...org, kind: 'label' })),
    ...(user?.organizations?.publishers || []).map((org) => ({ ...org, kind: 'publisher' }))];
  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>This page is for artists, labels and publishers</Typography>
      {organizations.length ? (
        <>
          <Typography color="text.secondary">Switch to one you run to use it.</Typography>
          {organizations.map((org) => (
            <Button key={`${org.kind}-${org.id}`} variant="contained" onClick={() => {
              workAsOrganization({ kind: org.kind, id: org.id, name: org.name });
              navigate(location.pathname);
            }}>
              Work as {org.name}
            </Button>
          ))}
        </>
      ) : (
        <>
          <Typography color="text.secondary">Release music from the account you already have.</Typography>
          <Button variant="contained" onClick={() => navigate(AppRoutes.StartArtistProfile)}>Start an artist profile</Button>
        </>
      )}
    </Box>
  );
};

export default CreatorOnlyRoute;
